import { apiClient, handleApiRequest } from './client'
import type { School, SchoolCreateRequest, SuperadminInfo, CountryInfo, RegionInfo } from './ultraadmin'

export interface PartnerCompanyPublic {
  id: string
  name: string
  address: string | null
  city: string | null
  zip_code: string | null
  country: string | null
  phone: string | null
  email: string | null
}

export interface PartnerCompanyAdmin extends PartnerCompanyPublic {
  notes: string | null
  commission_rate: number | null
  is_active: boolean
  created_at: string
  updated_at: string
  members: PartnerMember[]
  bot_webhook_url?: string | null
  bot_webhook_auth?: string | null
  bot_stopped?: boolean
  bot_name?: string | null
  bot_provision_error?: string | null
}

export interface PartnerMember {
  id: string
  user_id: string
  email: string
  first_name: string | null
  last_name: string | null
  level: 'owner' | 'staff'
  is_active: boolean
  created_at: string
}

export const partnerApi = {
  listSchools: () => handleApiRequest<School[]>(apiClient.get('/api/v1/partner/schools')),
  getSchool: (id: string) => handleApiRequest<School>(apiClient.get(`/api/v1/partner/schools/${id}`)),
  createSchool: (data: SchoolCreateRequest) =>
    handleApiRequest<School>(apiClient.post('/api/v1/partner/schools', data)),
  updateSchool: (id: string, data: Partial<SchoolCreateRequest>) =>
    handleApiRequest<School>(apiClient.patch(`/api/v1/partner/schools/${id}`, data)),
  addDomain: (schoolId: string, domain: string, is_primary = false) =>
    handleApiRequest(apiClient.post(`/api/v1/partner/schools/${schoolId}/domains`, { domain, is_primary })),
  removeDomain: (schoolId: string, domainId: string) =>
    handleApiRequest(apiClient.delete(`/api/v1/partner/schools/${schoolId}/domains/${domainId}`)),
  unlinkSchool: (schoolId: string, confirmation_name: string) =>
    handleApiRequest(apiClient.post(`/api/v1/partner/schools/${schoolId}/unlink`, { confirmation_name })),
  getCountries: () => handleApiRequest<CountryInfo[]>(apiClient.get('/api/v1/partner/countries')),
  getRegions: (code: string) =>
    handleApiRequest<RegionInfo[]>(apiClient.get(`/api/v1/partner/countries/${code}/regions`)),
  listSuperadmins: (schoolId?: string) => {
    const q = schoolId ? `?school_id=${schoolId}` : ''
    return handleApiRequest<SuperadminInfo[]>(apiClient.get(`/api/v1/partner/superadmins${q}`))
  },
  createSuperadmin: (data: { email: string; first_name: string; last_name: string; school_id: string }) =>
    handleApiRequest(apiClient.post('/api/v1/partner/superadmins', data)),
  deactivateSuperadmin: (id: string) =>
    handleApiRequest(apiClient.delete(`/api/v1/partner/superadmins/${id}`)),
  activateSuperadmin: (id: string) =>
    handleApiRequest(apiClient.post(`/api/v1/partner/superadmins/${id}/activate`)),
  resendInvite: (id: string) =>
    handleApiRequest(apiClient.post(`/api/v1/partner/superadmins/${id}/resend-invite`)),
  transferSuperadmin: (id: string, target_user_id: string) =>
    handleApiRequest(apiClient.post(`/api/v1/partner/superadmins/${id}/transfer`, { target_user_id })),
  getCompany: () => handleApiRequest<PartnerCompanyPublic>(apiClient.get('/api/v1/partner/company')),
  patchCompany: (data: Partial<PartnerCompanyPublic>) =>
    handleApiRequest<PartnerCompanyPublic>(apiClient.patch('/api/v1/partner/company', data)),
  listTeam: () => handleApiRequest<PartnerMember[]>(apiClient.get('/api/v1/partner/team')),
  inviteMember: (data: { email: string; first_name: string; last_name: string; level: 'owner' | 'staff' }) =>
    handleApiRequest<PartnerMember>(apiClient.post('/api/v1/partner/team', data)),
  updateMember: (userId: string, data: { level?: 'owner' | 'staff'; is_active?: boolean; first_name?: string; last_name?: string; email?: string }) =>
    handleApiRequest<PartnerMember>(apiClient.patch(`/api/v1/partner/team/${userId}`, data)),
  resendMemberInvite: (userId: string) =>
    handleApiRequest(apiClient.post(`/api/v1/partner/team/${userId}/resend-invite`)),
  listAudit: () => handleApiRequest(apiClient.get('/api/v1/partner/audit')),
  listSchoolUsers: (schoolId: string) =>
    handleApiRequest<{ users: { id: string; email: string; first_name: string | null; last_name: string | null; role: string }[] }>(
      apiClient.get(`/api/v1/partner/schools/${schoolId}/users`),
    ),
}

export const ultraadminPartnersApi = {
  list: () => handleApiRequest<PartnerCompanyAdmin[]>(apiClient.get('/api/v1/ultraadmin/partner-companies')),
  get: (id: string) => handleApiRequest<PartnerCompanyAdmin>(apiClient.get(`/api/v1/ultraadmin/partner-companies/${id}`)),
  create: (data: {
    name: string
    commission_rate?: number | null
    notes?: string
    owner: { email: string; first_name: string; last_name: string }
  }) => handleApiRequest<PartnerCompanyAdmin>(apiClient.post('/api/v1/ultraadmin/partner-companies', data)),
  update: (id: string, data: Partial<PartnerCompanyAdmin>) =>
    handleApiRequest<PartnerCompanyAdmin>(apiClient.patch(`/api/v1/ultraadmin/partner-companies/${id}`, data)),
  deactivate: (id: string) =>
    handleApiRequest(apiClient.post(`/api/v1/ultraadmin/partner-companies/${id}/deactivate`)),
  inviteMember: (companyId: string, data: { email: string; first_name: string; last_name: string; level: 'owner' | 'staff' }) =>
    handleApiRequest(apiClient.post(`/api/v1/ultraadmin/partner-companies/${companyId}/members`, data)),
  updateMember: (companyId: string, userId: string, data: { level?: string; is_active?: boolean; first_name?: string; last_name?: string; email?: string }) =>
    handleApiRequest(apiClient.patch(`/api/v1/ultraadmin/partner-companies/${companyId}/members/${userId}`, data)),
  assignSchool: (schoolId: string, partner_company_id: string | null) =>
    handleApiRequest(apiClient.post(`/api/v1/ultraadmin/schools/${schoolId}/assign-partner`, { partner_company_id })),
}
